import React, { useState, useRef, useEffect } from "react";
import "./multimodel.css";

const API_BASE = "http://localhost:3001";
// "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net";
const ENDPOINT = `${API_BASE}/api/multi-model/analyze`;

const MultiModel = () => {
  const [selectedImages, setSelectedImages] = useState([]);
  const [results, setResults] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [loaderMessageIndex, setLoaderMessageIndex] = useState(0);
  const fileInputRef = useRef(null);
  const resultsRef = useRef(null);
  const companyId = localStorage.getItem("company_id");

  const loaderMessages = [
    "Analyzing image",
    "Detecting brand",
    "Checking authenticity",
    "Assessing condition",
    "Estimating market price",
    "Consolidating results",
  ];

  // Rotate loader messages every 2 seconds while processing
  useEffect(() => {
    if (!isProcessing) return;
    const interval = setInterval(() => {
      setLoaderMessageIndex((prev) => (prev + 1) % loaderMessages.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [isProcessing, loaderMessages.length]);

  // ============================================================
  // Drop zone + file selection
  // ============================================================
  const handleDropZoneClick = () => {
    fileInputRef.current.click();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    handleFilesSelection(files);
  };

  const handleFilesSelection = (files) => {
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));
    imageFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImages((prev) => [
          ...prev,
          { file, preview: reader.result },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClearAll = () => {
    setSelectedImages([]);
    setResults([]);
    setError(null);
  };

  // ============================================================
  // Process: send to /api/multi-model/analyze
  // ============================================================
  const handleProcessImages = async () => {
    if (selectedImages.length === 0) {
      alert("No images to process. Please upload images first.");
      return;
    }

    setIsProcessing(true);
    setLoaderMessageIndex(0);
    setError(null);

    try {
      // Send ALL images in ONE request — field name MUST be "images" (plural)
      const formData = new FormData();
      formData.append("companyid", companyId);
      selectedImages.forEach((img) => {
        formData.append("images", img.file); // ← "images" plural
      });

      const response = await fetch(ENDPOINT, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Server error ${response.status}: ${text}`);
      }

      const data = await response.json();

      const result = mapApiResponseToResult(data, selectedImages[0]);
      result.allImages = selectedImages.map((img) => img.preview);
      result.imageCount = selectedImages.length;

      setResults((prev) => [...prev, result]);
      setSelectedImages([]);

      setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    } catch (err) {
      console.error("Multi-model error:", err);
      setError(err.message);
      alert("Multi-model analysis failed: " + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // ============================================================
  // Map multi-model API response → final consolidated result
  // ============================================================
  const mapApiResponseToResult = (data, img) => {
    const modelResults = data.results || {};
    const modelEntries = Object.entries(modelResults);

    let bestBrand = { name: "Unknown", confidence: 0, evidence: "" };
    let bestCondition = {
      grade: "Unknown",
      confidence: 0,
      issues: [],
      notes: "",
    };
    let bestAuth = {
      verdict: "Unknown",
      confidence: 0,
      red_flags: [],
      positive_signs: [],
    };
    let priceLows = [];
    let priceHighs = [];

    const allConditionIssues = new Set();
    const allConditionNotes = new Set();
    const allAuthRedFlags = new Set();
    const allAuthPositiveSigns = new Set();

    modelEntries.forEach(([_, modelData]) => {
      if (!modelData?.parsed) return;
      const p = modelData.parsed;

      if (p.brand?.confidence > bestBrand.confidence) bestBrand = p.brand;
      if (p.condition?.confidence > bestCondition.confidence)
        bestCondition = p.condition;
      if (p.authenticity?.confidence > bestAuth.confidence)
        bestAuth = p.authenticity;
      if (typeof p.price?.low_usd === "number") priceLows.push(p.price.low_usd);
      if (typeof p.price?.high_usd === "number")
        priceHighs.push(p.price.high_usd);

      (p.condition?.issues || []).forEach(
        (i) => i && allConditionIssues.add(i),
      );
      if (p.condition?.notes) allConditionNotes.add(p.condition.notes);
      (p.authenticity?.red_flags || []).forEach(
        (i) => i && allAuthRedFlags.add(i),
      );
      (p.authenticity?.positive_signs || []).forEach(
        (i) => i && allAuthPositiveSigns.add(i),
      );
    });

    const avgLow = priceLows.length
      ? Math.round(priceLows.reduce((a, b) => a + b) / priceLows.length)
      : 0;
    const avgHigh = priceHighs.length
      ? Math.round(priceHighs.reduce((a, b) => a + b) / priceHighs.length)
      : 0;
    const avgPrice = Math.round((avgLow + avgHigh) / 2);

    const conditionScoreMap = {
      new: 10,
      like_new: 9,
      excellent: 8,
      good: 7,
      fair: 6,
      poor: 4,
    };
    const conditionScore = conditionScoreMap[bestCondition.grade] || 0;
    const authScore = Math.round((bestAuth.confidence || 0) * 100);

    const authReasons = [];
    Array.from(allAuthPositiveSigns)
      .slice(0, 5)
      .forEach((sign) => {
        authReasons.push({ status: "pass", text: sign });
      });
    Array.from(allAuthRedFlags)
      .slice(0, 5)
      .forEach((flag) => {
        authReasons.push({ status: "warning", text: flag });
      });
    if (authReasons.length === 0) {
      authReasons.push({
        status: bestAuth.verdict?.includes("authentic") ? "pass" : "warning",
        text: `Verdict: ${bestAuth.verdict || "Cannot determine"}`,
      });
    }

    const conditionReasons = [];
    Array.from(allConditionIssues)
      .slice(0, 6)
      .forEach((issue) => {
        conditionReasons.push({ status: "warning", text: issue });
      });
    Array.from(allConditionNotes)
      .slice(0, 3)
      .forEach((note) => {
        conditionReasons.push({ status: "pass", text: note });
      });
    if (conditionReasons.length === 0) {
      conditionReasons.push({
        status: "pass",
        text: `Grade: ${bestCondition.grade || "Unknown"}`,
      });
    }

    return {
      image: img.preview,
      fileName: img.file?.name || "Uploaded Image",
      description:
        bestBrand.name !== "Unknown" ? bestBrand.name : "Unknown Product",
      price: avgPrice,
      priceRange: avgLow && avgHigh ? `$${avgLow} - $${avgHigh}` : null,
      authScore,
      conditionScore,
      suggestion: getSuggestion(authScore, conditionScore),
      authReasons,
      conditionReasons,
    };
  };

  // ============================================================
  // Suggestion logic
  // Below 80% auth OR below 8/10 condition → Wholesale
  // ============================================================
  const getSuggestion = (authScore, conditionScore) => {
    if (authScore < 80 || conditionScore < 8) {
      return "Wholesale";
    }
    return "Sell Online";
  };

  // ============================================================
  // Modal
  // ============================================================
  const openReasonModal = (type, score, reasons, description) => {
    setModalData({ type, score, reasons: reasons || [], description });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalData(null);
  };

  const handleSellOnline = () =>
    alert("Sell Online action — not wired for test page");

  // ============================================================
  // Render
  // ============================================================
  return (
    <>
      {/* Full-screen loader overlay */}
      {isProcessing && (
        <div className="multimodel-loader-overlay">
          <div className="multimodel-loader-content">
            <div className="multimodel-spinner"></div>
            <h2 className="multimodel-loader-title">
              {loaderMessages[loaderMessageIndex]}
            </h2>
            <p className="multimodel-loader-subtitle">
              Please wait, this usually takes 10-30 seconds...
            </p>
            <div className="multimodel-loader-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      )}

      <div className="sorter-page">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={(e) => handleFilesSelection(Array.from(e.target.files))}
        />
        <div className="sorter-container">
          {selectedImages.length === 0 ? (
            <div className="sorter-options">
              <div
                className="sorter-option-card drop-zone-card"
                onClick={handleDropZoneClick}
                onDragOver={handleDragOver}
                onDrop={handleDragDrop}
              >
                <div className="upload-placeholder">
                  <div className="upload-icon">📷</div>
                  <h3>Drop Images OR Click to Upload</h3>
                  <p>JPG, PNG, WebP • Max 5MB</p>
                </div>
              </div>
            </div>
          ) : null}

          {selectedImages.length > 0 && (
            <div className="image-preview-section">
              <div className="images-grid">
                {selectedImages.map((img, index) => (
                  <div key={index} className="preview-card">
                    <button
                      className="remove-btn"
                      onClick={() => handleRemoveImage(index)}
                      disabled={isProcessing}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                    <img
                      src={img.preview}
                      alt={`Selected ${index + 1}`}
                      className="preview-image"
                    />
                    <div className="preview-info">
                      <p className="file-name">
                        {img.file?.name || `Photo ${index + 1}`}
                      </p>
                      <p className="file-size">
                        {(img.file?.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="preview-actions">
                <button
                  className="btn-secondary"
                  onClick={handleClearAll}
                  disabled={isProcessing}
                  style={{ touchAction: "manipulation" }}
                >
                  Clear All ({selectedImages.length})
                </button>
                <button
                  className="btn-primary"
                  onClick={handleProcessImages}
                  disabled={isProcessing}
                  style={{ touchAction: "manipulation" }}
                >
                  {isProcessing
                    ? `⏳ Processing...`
                    : `Process ${selectedImages.length} Image${selectedImages.length !== 1 ? "s" : ""}`}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="multimodel-error-box">
              <strong>❌ Error:</strong> {error}
            </div>
          )}

          {results.length > 0 && (
            <div className="results-section" ref={resultsRef}>
              <h2 className="results-title">Processing Results</h2>
              <div className="result-table-wrapper">
                <table className="result-table">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Product Description</th>
                      <th>Potential Price</th>
                      <th>Authentication Score</th>
                      <th>Condition Score</th>
                      <th>Thriftops Suggestion</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((result, index) => (
                      <tr key={index} className="result-row">
                        <td className="image-cell" data-label="Image">
                          <img
                            src={result.image}
                            alt={result.fileName}
                            className="result-thumbnail"
                          />
                        </td>
                        <td
                          className="description-cell"
                          data-label="Product Description"
                        >
                          <div className="description-text">
                            {result.description}
                          </div>
                          <div className="file-name-small">
                            {result.fileName}
                          </div>
                        </td>
                        <td className="price-cell" data-label="Potential Price">
                          <span className="price-value">${result.price}</span>
                          {result.priceRange && (
                            <div className="price-range-text">
                              {result.priceRange}
                            </div>
                          )}
                        </td>
                        <td
                          className="score-cell"
                          data-label="Authentication Score"
                        >
                          <div className="score-with-icon">
                            <div className="score-badge score-auth">
                              {result.authScore}%
                            </div>
                            <button
                              className="eye-icon-btn"
                              onClick={() =>
                                openReasonModal(
                                  "Authentication",
                                  result.authScore,
                                  result.authReasons,
                                  result.description,
                                )
                              }
                              title="View authentication details"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                            </button>
                          </div>
                        </td>
                        <td className="score-cell" data-label="Condition Score">
                          <div className="score-with-icon">
                            <div className="score-badge score-condition">
                              {result.conditionScore}/10
                            </div>
                            <button
                              className="eye-icon-btn"
                              onClick={() =>
                                openReasonModal(
                                  "Condition",
                                  result.conditionScore,
                                  result.conditionReasons,
                                  result.description,
                                )
                              }
                              title="View condition details"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                            </button>
                          </div>
                        </td>
                        <td
                          className="suggestion-cell"
                          data-label="Thriftops Suggestion"
                        >
                          <span className="suggestion-text">
                            {result.suggestion}
                          </span>
                        </td>
                        <td className="actions-cell" data-label="Actions">
                          <div className="action-buttons">
                            <button
                              className="btn-primary btn-sm"
                              onClick={handleSellOnline}
                              title="Sell online"
                            >
                              Sell Online
                            </button>
                            <button
                              className="btn-secondary btn-sm"
                              disabled
                              title="Disabled"
                            >
                              Push to Retail
                            </button>
                            <button
                              className="btn-secondary btn-sm"
                              disabled
                              title="Disabled"
                            >
                              Wholesale
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Reason Modal */}
          {modalOpen && modalData && (
            <div className="reason-modal-overlay" onClick={closeModal}>
              <div
                className="reason-modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <div className="modal-title-row">
                    <div className="modal-title-section">
                      <h2 className="modal-product-title">
                        {modalData.description}
                      </h2>
                      <h3 className="modal-analysis-type">
                        {modalData.type} Analysis
                      </h3>
                    </div>
                    <div className="modal-score">
                      <span className="score-label">Score</span>
                      <span
                        className={`score-value ${modalData.type === "Authentication" ? "score-auth" : "score-condition"}`}
                      >
                        {modalData.score}
                        {modalData.type === "Authentication" ? "%" : "/10"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="modal-body">
                  <h4 className="reasons-heading">Key Points:</h4>
                  <ul className="reasons-list">
                    {modalData.reasons && modalData.reasons.length > 0 ? (
                      modalData.reasons.map((reason, idx) => (
                        <li
                          key={idx}
                          className={`reason-item reason-${reason.status}`}
                        >
                          <span className="reason-icon">
                            {reason.status === "pass" ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                <line x1="12" y1="9" x2="12" y2="13"></line>
                                <line x1="12" y1="17" x2="12.01" y2="17"></line>
                              </svg>
                            )}
                          </span>
                          <span className="reason-text">{reason.text}</span>
                        </li>
                      ))
                    ) : (
                      <li className="reason-item reason-pass">
                        <span className="reason-text">
                          No detailed analysis available.
                        </span>
                      </li>
                    )}
                  </ul>
                </div>
                <div className="modal-footer">
                  <button className="modal-btn-close" onClick={closeModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MultiModel;
